import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Stack,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    Alert,
} from "@mui/material";
import toast from "react-hot-toast";
import Iconify from "../../../components/iconify/Iconify";
import EntityConstants from "../../../configs/EntityConstants";
import SpinService from "../../../services/SpinService";
import { post_form_data } from "../../../services/FetchHelper";

const ResultSection = ({ title, items, color, icon }) => {
    if (!items || items.length === 0) return null;
    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Iconify icon={icon} sx={{ color }} />
                    <Typography variant="subtitle1" sx={{ color, fontWeight: 600 }}>
                        {title} ({items.length})
                    </Typography>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <List dense sx={{ maxHeight: 240, overflowY: "auto" }}>
                    {items.map((item, i) => (
                        <ListItem key={i} sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={typeof item === "string" ? item : item.invoice_no}
                                secondary={typeof item === "object" ? item.reason : null}
                            />
                        </ListItem>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};

const InvoiceDeletion = () => {
    const navigate = useNavigate();
    const spinService = SpinService();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        const userTag = sessionStorage.getItem("user_tag");
        if (userTag && userTag.toUpperCase().includes("PRE")) {
            navigate("/");
        }
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please upload an .xlsx file.");
            return;
        }
        setSelectedFile(file);
        setResult(null);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error("Pick a file first.");
            return;
        }
        const confirmed = window.confirm(
            "This will permanently delete the listed invoices, including their lines and journal entries. Continue?"
        );
        if (!confirmed) return;

        spinService.on();
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const response = await post_form_data(
                EntityConstants.BULK_DELETE_INVOICES,
                formData
            );

            const data = await response.json();
            if (response.ok) {
                setResult(data);
                toast.success(
                    `Deleted ${data.deleted_count} invoice(s).` +
                        (data.not_found_count > 0
                            ? ` ${data.not_found_count} not found.`
                            : "") +
                        (data.error_count > 0
                            ? ` ${data.error_count} errors.`
                            : "")
                );
            } else {
                toast.error(data.error || "Upload failed.");
            }
        } catch (err) {
            toast.error(`Upload failed: ${err.message}`);
        } finally {
            spinService.off();
        }
    };

    const handleReset = () => {
        setSelectedFile(null);
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <Container maxWidth="md">
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Invoice Deletion</Typography>
            </Stack>

            <Alert severity="warning" sx={{ mb: 2 }}>
                Upload an <b>.xlsx</b> file with an <b>Invoice No</b> column. Every listed invoice will be
                permanently deleted along with its lines, journal entries, and links from receipts.
                Frozen invoices will also be deleted &mdash; this operation cannot be undone.
            </Alert>

            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Iconify icon="mdi:file-excel-outline" />}
                    >
                        Choose XLSX File
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                    {selectedFile && (
                        <Chip
                            label={selectedFile.name}
                            onDelete={handleReset}
                            color="default"
                            variant="outlined"
                        />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<Iconify icon="mdi:delete-forever" />}
                        onClick={handleUpload}
                        disabled={!selectedFile}
                    >
                        Upload &amp; Delete
                    </Button>
                </Stack>
            </Card>

            {result && (
                <>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Result
                    </Typography>
                    <ResultSection
                        title="Deleted"
                        items={result.deleted}
                        color="success.main"
                        icon="mdi:check-circle-outline"
                    />
                    <ResultSection
                        title="Not Found"
                        items={result.not_found}
                        color="warning.main"
                        icon="mdi:alert-circle-outline"
                    />
                    <ResultSection
                        title="Errors"
                        items={result.errors}
                        color="error.main"
                        icon="mdi:close-circle-outline"
                    />
                </>
            )}
        </Container>
    );
};

export default InvoiceDeletion;
